/**
 * Banner de período de teste do plano Free
 *
 * Redesign Notion-like: pílula neutra com indicador de status (bolinha colorida)
 * + CTA sólido baseado em tokens semânticos. Sem fundos coloridos chamativos.
 * Apenas visível para usuários no plano Free com trial ativo.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInDays, parseISO } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TrialBannerProps {
  /** Data de expiração do trial (ISO string) */
  trialEndsAt: string | null;
  /** Nome do plano atual (enum legado em `tenants.plan`) */
  planName: string | null;
  /** Status do tenant */
  tenantStatus: string | null;
  /**
   * v1.1.0 — Quando true, oculta o banner por completo.
   *
   * Usado quando o tenant já está no novo modelo de planos
   * (`tenant_billing_bindings` com `billing_integration = 'integracao_v1'`),
   * pois nessa arquitetura a sinalização de trial / past_due / dunning é
   * feita pelo `TenantBillingBanner` dentro do `PaymentGuard`, evitando
   * mensagens duplicadas e dependência do enum legado `tenants.plan`.
   */
  hideForIntegracaoV1?: boolean;
}

/**
 * Retorna a cor semântica do indicador de status (bolinha)
 * baseada nos dias restantes — destaque é cromático apenas no DOT,
 * não no fundo da pílula inteira.
 */
const getStatusDot = (days: number) => {
  if (days <= 0) {
    return { color: 'bg-destructive', pulse: true };
  }
  if (days <= 2) {
    return { color: 'bg-destructive', pulse: false };
  }
  if (days <= 6) {
    return { color: 'bg-amber-500', pulse: false };
  }
  return { color: 'bg-emerald-500', pulse: false };
};

/**
 * Mensagem humanizada com destaque tipográfico no número de dias.
 * Retorna partes separadas para permitir estilizar o número.
 */
const getMessageParts = (days: number): { prefix: string; highlight: string; suffix: string } => {
  if (days <= 0) return { prefix: 'Período de teste', highlight: 'encerrado', suffix: '' };
  if (days === 1) return { prefix: 'Último dia', highlight: 'de teste', suffix: '' };
  if (days <= 2) return { prefix: 'Restam', highlight: `${days} dias`, suffix: '' };
  if (days <= 6) return { prefix: 'Restam apenas', highlight: `${days} dias`, suffix: '' };
  return { prefix: '', highlight: `${days} dias`, suffix: 'restantes' };
};

/**
 * Texto do CTA adaptado à urgência
 */
const getCtaText = (days: number): string => {
  if (days <= 0) return 'Ativar plano';
  if (days <= 2) return 'Garantir acesso';
  return 'Fazer upgrade';
};

export function TrialBanner({ trialEndsAt, planName, tenantStatus, hideForIntegracaoV1 }: TrialBannerProps) {
  // v1.1.0 — Tenant já está no novo modelo (integracao_v1): TenantBillingBanner
  // assume o papel de comunicar trial/past_due/dunning; banner legado some.
  if (hideForIntegracaoV1) return null;

  const navigate = useNavigate();

  // Calcula dias restantes e determina se deve exibir o banner
  const bannerData = useMemo(() => {
    // Só exibe para plano Free com trial definido
    const isFree = planName?.toLowerCase() === 'free';

    if (!isFree) return null;
    if (!trialEndsAt) return null;

    try {
      const trialEnd = parseISO(trialEndsAt);
      const now = new Date();
      const daysRemaining = differenceInDays(trialEnd, now);

      return {
        days: daysRemaining,
        dot: getStatusDot(daysRemaining),
        parts: getMessageParts(daysRemaining),
        cta: getCtaText(daysRemaining),
        isExpired: daysRemaining <= 0,
      };
    } catch {
      return null;
    }
  }, [trialEndsAt, planName]);

  // Não renderiza se não houver dados de trial
  if (!bannerData) return null;

  const { dot, parts, cta, isExpired } = bannerData;

  return (
    <div className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border border-border bg-card shadow-sm">
      {/* Indicador de status (bolinha colorida com pulse opcional) */}
      <span className="relative flex h-2 w-2 shrink-0">
        {dot.pulse && (
          <span
            className={cn(
              'absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping',
              dot.color
            )}
          />
        )}
        <span className={cn('relative inline-flex h-2 w-2 rounded-full', dot.color)} />
      </span>

      {/* Mensagem com destaque tipográfico no número de dias */}
      <span className="text-xs sm:text-sm font-medium text-muted-foreground whitespace-nowrap">
        {parts.prefix && <>{parts.prefix} </>}
        <span className="font-semibold text-foreground">{parts.highlight}</span>
        {parts.suffix && <> {parts.suffix}</>}
      </span>

      {/* Divisor vertical sutil */}
      <span className="w-px h-4 bg-border mx-0.5" aria-hidden="true" />

      {/* CTA sólido em pill compacto */}
      <button
        type="button"
        onClick={() => navigate('/tenant/plans')}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold transition-colors',
          isExpired
            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        )}
      >
        {cta}
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
